import {DataTableColumn} from 'mantine-datatable';
import {I18n} from '@aws-amplify/core';
import {Text} from '@mantine/core';
import {PaymentEvent} from '../types';
import {formatTimestamp, TIMESTAMP_PLACEHOLDER} from '../../../utils/TableTimestamps';
import {PaymentEventStatusBadge} from '../components/PaymentEventStatusBadge';

// `amount` is an Int in minor units (cents).
const formatAmount = (amount?: number, currency?: string) => {
    if (typeof amount !== 'number') return TIMESTAMP_PLACEHOLDER;
    const display = (amount / 100).toFixed(2);
    return currency ? `${display} ${currency.toUpperCase()}` : display;
};

// Every column is sortable (§3a). The `accessor` is what sorts: `amount` sorts on the
// raw number, not the formatted string, and the timestamps sort as ISO-8601.
// Empty values render the shared placeholder, never a blank cell (§13).
//
// No `created` column: the default sort is created DESC (usePaymentEvents, the API's own
// order) without showing it, because a sixth column overflows the invoice page's tab panel
// at 1440px (checked in Chromium, FNP-1880). Any header click re-sorts from there.
export const usePaymentEventsTableColumns = () => {

    const columns: DataTableColumn<PaymentEvent>[] = [
        {
            accessor: 'status',
            title: I18n.get('Status'),
            sortable: true,
            render: (record: PaymentEvent) => <PaymentEventStatusBadge status={record.status}/>,
        },
        {
            accessor: 'amount',
            title: I18n.get('Amount'),
            sortable: true,
            render: (record: PaymentEvent) => formatAmount(record.amount, record.currency),
        },
        {
            accessor: 'paymentMethodLast4',
            title: I18n.get('Card last 4'),
            sortable: true,
            render: (record: PaymentEvent) => record.paymentMethodLast4 ? `•••• ${record.paymentMethodLast4}` : TIMESTAMP_PLACEHOLDER,
        },
        {
            accessor: 'failureMessage',
            title: I18n.get('Failure reason'),
            sortable: true,
            render: (record: PaymentEvent) => record.failureMessage ? (
                <Text size='sm' c='red'>{record.failureMessage}</Text>
            ) : TIMESTAMP_PLACEHOLDER,
        },
        {
            accessor: 'occurredAt',
            title: I18n.get('Occurred at'),
            sortable: true,
            render: (record: PaymentEvent) => formatTimestamp(record.occurredAt),
        },
    ];

    return {columns};
};
